import { useLocation } from 'react-router-dom';

const { isYowanRoutePath } = require('../../domains/yowan/routes/manifest');

const YOWAN_PREVIEW_QUERY_KEY = 'domain';
const YOWAN_PREVIEW_QUERY_VALUE = 'yowan';
const DEVELOPMENT_ENVIRONMENT = 'development';

// app/host responsibility:
// - host-based routing belongs to RootApp / resolveDomainApp
// - development-only Yowan preview query propagation belongs here
// This module is intentionally not a generic multi-domain utility. It exists to
// support local Yowan preview verification without leaking development-only
// query behavior into staging / production routing.

const normalizeSearch = (search = '') => {
  if (!search) {
    return '';
  }

  return search.startsWith('?') ? search.slice(1) : search;
};

const splitPathSearchHash = (target = '') => {
  const hashIndex = target.indexOf('#');
  const hash = hashIndex >= 0 ? target.slice(hashIndex) : '';
  const pathAndSearch = hashIndex >= 0 ? target.slice(0, hashIndex) : target;
  const searchIndex = pathAndSearch.indexOf('?');

  return {
    pathname: searchIndex >= 0 ? pathAndSearch.slice(0, searchIndex) : pathAndSearch,
    search: searchIndex >= 0 ? pathAndSearch.slice(searchIndex) : '',
    hash,
  };
};

export const isDevelopmentEnvironment = () =>
  process.env.REACT_APP_ENVIRONMENT === DEVELOPMENT_ENVIRONMENT;

export const getYowanPreviewSearch = (currentSearch = '') => {
  if (!isDevelopmentEnvironment()) {
    return '';
  }

  const searchParams = new URLSearchParams(normalizeSearch(currentSearch));
  return searchParams.get(YOWAN_PREVIEW_QUERY_KEY) === YOWAN_PREVIEW_QUERY_VALUE
    ? `?${YOWAN_PREVIEW_QUERY_KEY}=${YOWAN_PREVIEW_QUERY_VALUE}`
    : '';
};

export const isYowanPreviewActive = (currentSearch = '') =>
  Boolean(getYowanPreviewSearch(currentSearch));

export const isYowanInternalPath = (pathname = '') =>
  typeof pathname === 'string' &&
  pathname.startsWith('/') &&
  isYowanRoutePath(pathname);

const buildPreviewSearch = (targetSearch = '', currentSearch = '') => {
  if (!isYowanPreviewActive(currentSearch)) {
    return targetSearch || '';
  }

  const nextSearchParams = new URLSearchParams(normalizeSearch(targetSearch));
  nextSearchParams.set(YOWAN_PREVIEW_QUERY_KEY, YOWAN_PREVIEW_QUERY_VALUE);
  const nextQuery = nextSearchParams.toString();

  return nextQuery ? `?${nextQuery}` : '';
};

export const appendYowanPreviewQueryIfNeeded = (target, currentSearch = '') => {
  if (!isYowanPreviewActive(currentSearch)) {
    return target;
  }

  if (typeof target === 'string') {
    if (!target.startsWith('/')) {
      return target;
    }

    const { pathname, search, hash } = splitPathSearchHash(target);
    if (!isYowanInternalPath(pathname)) {
      return target;
    }

    return `${pathname}${buildPreviewSearch(search, currentSearch)}${hash}`;
  }

  if (!target || typeof target !== 'object' || !target.pathname) {
    return target;
  }

  if (!isYowanInternalPath(target.pathname)) {
    return target;
  }

  return {
    ...target,
    search: buildPreviewSearch(target.search, currentSearch),
  };
};

export const useYowanPreviewSearch = () => {
  const location = useLocation();
  return getYowanPreviewSearch(location.search);
};
